function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeMathjax from 'rehype-mathjax';
import { Button } from '@deephaven/components';
import { Code } from '@deephaven/console';
import { vsPlay } from '@deephaven/icons';
import "./MarkdownNotebook.css";
import { assertNotNull } from '@deephaven/utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export class MarkdownNotebook extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "commands", void 0);
    _defineProperty(this, "codeElements", void 0);
    _defineProperty(this, "editorScrollView", void 0);
    this.handleRunSelected = this.handleRunSelected.bind(this);
    this.renderCodeBlock = this.renderCodeBlock.bind(this);
    this.renderLink = this.renderLink.bind(this);

    // Map of each code block from it's starting line number to the code within that block
    this.commands = new Map();
    this.codeElements = new Map();
    this.editorScrollView = /*#__PURE__*/React.createRef();
    this.state = {
      hasCode: false,
      // Keep track if any code has been executed yet. If not, make the run button flash
      hasRunCode: false,
      // Line of the next block to execute. Null to start at the first block
      nextStartLine: null
    };
  }
  componentDidMount() {
    this.updateHasCode();
  }
  componentDidUpdate() {
    this.updateHasCode();
  }
  updateHasCode() {
    var hasCode = this.state.hasCode;
    if (this.commands.size === 0 && hasCode) {
      this.setState({
        hasCode: false
      });
    } else if (this.commands.size > 0 && !hasCode) {
      this.setState({
        hasCode: true
      });
    }
  }

  /**
   * @param line The line of the code block to start from
   * @returns The next line of the code block to start from
   */
  getNextStartLine(line) {
    var keys = [...this.commands.keys()];
    var nextIndex = keys.findIndex(key => key === line) + 1;
    if (nextIndex >= keys.length) {
      // We got to the last block, disable
      return null;
    }
    return keys[nextIndex];
  }
  handleRunSelected(event) {
    var _this$codeElements$ge, _element$parentElemen;
    event.preventDefault();
    event.stopPropagation();
    var nextStartLine = this.state.nextStartLine;
    var keys = [...this.commands.keys()];
    var keyIndex = keys.findIndex(key => key === nextStartLine || nextStartLine == null);
    if (keyIndex < 0) {
      return;
    }
    var startLine = keys[keyIndex];
    var command = this.commands.get(startLine);
    var newNextStartLine = this.getNextStartLine(startLine);
    var element = (_this$codeElements$ge = this.codeElements.get(startLine)) === null || _this$codeElements$ge === void 0 ? void 0 : _this$codeElements$ge.current;
    var nextElement = element === null || element === void 0 || (_element$parentElemen = element.parentElement) === null || _element$parentElemen === void 0 ? void 0 : _element$parentElemen.nextElementSibling;
    if (nextElement != null) {
      var _this$editorScrollVie;
      var _ref = nextElement,
        offsetTop = _ref.offsetTop;
      var top = offsetTop;
      (_this$editorScrollVie = this.editorScrollView.current) === null || _this$editorScrollVie === void 0 || _this$editorScrollVie.scroll({
        top,
        left: 0
      });
    }
    this.runCode(command);
    this.setState({
      nextStartLine: newNextStartLine
    });
  }
  runCode(command) {
    var onRunCode = this.props.onRunCode;
    onRunCode(command);
    this.setState({
      hasRunCode: true
    });
  }
  renderCodeBlock(props) {
    var children = props.children,
      className = props.className,
      inline = props.inline,
      node = props.node;
    var _this$state = this.state,
      hasRunCode = _this$state.hasRunCode,
      nextStartLine = _this$state.nextStartLine;
    var nodeChildren = node.children,
      position = node.position;
    assertNotNull(position);
    var start = position.start;
    var line = start.line;
    var command = nodeChildren[0].value;
    var ref = /*#__PURE__*/React.createRef();
    var isFirstBlock = this.commands.size === 0;
    var isSelected = nextStartLine === line || isFirstBlock && nextStartLine == null && !hasRunCode;
    var language = className !== undefined && className.startsWith('language-') ? className.substring(9) : 'plaintext';
    if (inline != null && inline) {
      return /*#__PURE__*/_jsx("code", {
        className: className,
        children: children
      });
    }
    this.commands.set(line, command);
    this.codeElements.set(line, ref);
    return /*#__PURE__*/_jsxs("div", {
      className: classNames('markdown-notebook-code-block', {
        'is-selected': isSelected
      }),
      ref: ref,
      onClick: () => {
        this.setState({
          nextStartLine: line
        });
      },
      role: "presentation",
      children: [/*#__PURE__*/_jsx(Button, {
        kind: "ghost",
        icon: vsPlay,
        className: "btn-play-block",
        onClick: event => {
          event.stopPropagation();
          event.preventDefault();
          this.runCode(command);
          this.setState({
            nextStartLine: line
          });
        },
        tooltip: "Run code"
      }), /*#__PURE__*/_jsx("code", {
        children: /*#__PURE__*/_jsx(Code, {
          language: language,
          children: React.Children.map(children, child => typeof child === 'string' ? child.trim() : child)
        })
      })]
    });
  }
  renderLink(props) {
    var onLinkClick = this.props.onLinkClick;
    var href = props.href,
      children = props.children,
      target = props.target;
    return /*#__PURE__*/_jsx("a", {
      href: href,
      onClick: onLinkClick,
      target: target,
      children: children
    });
  }
  render() {
    var _this$props = this.props,
      content = _this$props.content,
      transformImageUri = _this$props.transformImageUri,
      transformLinkUri = _this$props.transformLinkUri;
    var _this$state2 = this.state,
      hasCode = _this$state2.hasCode,
      hasRunCode = _this$state2.hasRunCode,
      nextStartLine = _this$state2.nextStartLine;
    return /*#__PURE__*/_jsxs("div", {
      className: "markdown-notebook",
      children: [/*#__PURE__*/_jsx("div", {
        className: "markdown-notebook-toolbar",
        children: /*#__PURE__*/_jsx(Button, {
          className: classNames('btn-play-selected-cell', {
            flashing: hasCode && !hasRunCode
          }),
          kind: "ghost",
          icon: vsPlay,
          onClick: this.handleRunSelected,
          tooltip: "Run code and select next",
          disabled: hasRunCode && nextStartLine == null || !hasCode,
          children: "Run Selected Code"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "markdown-notebook-content",
        ref: this.editorScrollView,
        children: /*#__PURE__*/_jsx(Markdown, {
          components: {
            code: this.renderCodeBlock,
            a: this.renderLink
          },
          linkTarget: "_blank",
          remarkPlugins: [remarkGfm, remarkMath],
          rehypePlugins: [rehypeMathjax],
          transformLinkUri: transformLinkUri,
          transformImageUri: transformImageUri,
          includeElementIndex: true,
          children: content
        })
      })]
    });
  }
}
_defineProperty(MarkdownNotebook, "defaultProps", {
  onRunCode: () => undefined
});
export default MarkdownNotebook;
//# sourceMappingURL=MarkdownNotebook.js.map